import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MessageCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { FieldLabel } from '../../components/ui/Field'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'

/**
 * [Telegram bot] Master Admin → the PLATFORM's single bot connection.
 *
 * Configured ONCE here and shared by every company: there is no per-company bot
 * or per-company webhook. Members authenticate to their own TaskMagix account
 * from inside Telegram via a single-use deep link, so this page owns only the
 * credential and the delivery endpoint.
 *
 * The bot token is never returned by the API — only a mask — so the field stays
 * blank unless a new token is being entered.
 */
const BASE = '/super-admin/telegram'
const QUERY_KEY = ['super-admin', 'telegram']

const inputCls =
  'w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]'

function Toggle({ checked, onChange, labelOn, labelOff }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 cursor-pointer bg-transparent border-0 p-0"
    >
      <span
        className="relative inline-block h-5 w-9 rounded-full transition-colors"
        style={{
          background: checked
            ? 'var(--color-accent)'
            : 'var(--color-rule-strong, var(--color-rule))',
        }}
      >
        <span
          className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
          style={{ left: checked ? '1.125rem' : '0.125rem' }}
        />
      </span>
      <span className="text-xs text-[color:var(--color-ink-muted)]">
        {checked ? labelOn : labelOff}
      </span>
    </button>
  )
}

export function TelegramIntegrationPage() {
  const qc = useQueryClient()
  const [botToken, setBotToken] = useState('')
  const [botTokenDirty, setBotTokenDirty] = useState(false)

  const configQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => (await api.get(BASE)).data,
  })
  const cfg = configQuery.data

  const saveToken = useMutation({
    mutationFn: async () => {
      await api.put(`${BASE}/token`, { botToken: botToken.trim() })
    },
    onSuccess: () => {
      toast.success('Bot token saved.')
      setBotToken('')
      setBotTokenDirty(false)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      toast.error(fields?.botToken ?? message)
    },
  })

  const configureWebhook = useMutation({
    mutationFn: async () => {
      await api.post(`${BASE}/webhook`)
    },
    onSuccess: () => {
      toast.success('Webhook registered with Telegram.')
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const setEnabled = useMutation({
    mutationFn: async (enabled) => {
      await api.post(`${BASE}/enabled`, { enabled })
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const hasToken = Boolean(cfg?.hasToken)
  const enabled = Boolean(cfg?.enabled)
  const live = Boolean(cfg?.webhookConfigured && cfg?.enabled)
  const canEnable = hasToken && Boolean(cfg?.webhookConfigured)
  const tokenPlaceholder =
    hasToken && !botTokenDirty
      ? `${cfg?.tokenMasked ?? '•••••••••'} — leave blank to keep`
      : 'Paste the token from @BotFather (e.g. 1234567:ABC…)'

  return (
    <div className="space-y-6">
      <Breadcrumb current="Telegram Integration" homePath="/super-admin/dashboard" />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <MessageCircle size={20} className="text-[color:var(--color-accent-strong)]" />
            Telegram Integration
          </h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
            One bot for the whole platform. Members sign in to their own TaskMagix account
            from Telegram and create tasks in the Lists they can already access.
          </p>
        </div>
        {!configQuery.isLoading ? (
          <span
            className={
              'shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ' +
              (live
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10'
                : 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]')
            }
          >
            <span
              aria-hidden="true"
              className={
                'h-1.5 w-1.5 rounded-full ' +
                (live ? 'bg-emerald-500' : 'bg-[color:var(--color-ink-muted)]')
              }
            />
            {live ? 'Live' : enabled ? 'Enabled' : 'Not configured'}
          </span>
        ) : null}
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading configuration…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 space-y-5">
            <div>
              <FieldLabel label="Bot token" htmlFor="tg-token" />
              <input
                id="tg-token"
                type="password"
                autoComplete="off"
                placeholder={tokenPlaceholder}
                value={botToken}
                onChange={(e) => {
                  setBotToken(e.target.value)
                  setBotTokenDirty(true)
                }}
                maxLength={200}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Encrypted at rest (AES-256-GCM) and never returned after saving. Validated
                with Telegram when you save.
                {cfg?.botUsername ? ` Connected as @${cfg.botUsername}.` : ''}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => saveToken.mutate()}
                loading={saveToken.isPending}
                disabled={!botToken.trim()}
              >
                Save token
              </Button>
              <Button
                variant="secondary"
                onClick={() => configureWebhook.mutate()}
                loading={configureWebhook.isPending}
                disabled={!hasToken}
                title={
                  !hasToken
                    ? 'Save a bot token first.'
                    : 'Register this platform’s webhook URL with Telegram.'
                }
              >
                {cfg?.webhookConfigured ? 'Re-register webhook' : 'Register webhook'}
              </Button>
            </div>

            <div className="pt-4 border-t border-[color:var(--color-rule)] flex items-center justify-between gap-4">
              <div>
                <FieldLabel label="Bot status" />
                <p className="text-xs text-[color:var(--color-ink-muted)] m-0">
                  {canEnable
                    ? 'When enabled, members across all companies can use the bot.'
                    : 'Save a token and register the webhook to enable the bot.'}
                </p>
              </div>
              <Toggle
                checked={enabled}
                onChange={(next) => {
                  if (next && !canEnable) {
                    toast.error('Save a bot token and register the webhook first.')
                    return
                  }
                  setEnabled.mutate(next)
                }}
                labelOn="Enabled"
                labelOff="Disabled"
              />
            </div>

            {cfg?.updatedAt ? (
              <p className="text-xs text-[color:var(--color-ink-muted)] m-0">
                Last updated {formatDateTime(cfg.updatedAt)}
              </p>
            ) : null}
          </section>

          <aside className="space-y-4">
            <section className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
              <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
                How This Works
              </h3>
              <ol className="text-xs text-[color:var(--color-ink)] space-y-1.5 list-decimal pl-4 m-0">
                <li>Create one bot with @BotFather and paste its token here.</li>
                <li>Register the webhook, then enable the bot.</li>
                <li>
                  A member opens TaskMagix → Connect Telegram and taps the one-time link —
                  that signs them in without typing a password.
                </li>
                <li>
                  In the bot they pick a Space, then a Project or List, then send the task
                  name. Only the Lists they can already access are offered.
                </li>
              </ol>
            </section>

            <section className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
              <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
                Requirements
              </h3>
              <ul className="text-xs text-[color:var(--color-ink-muted)] space-y-1.5 list-disc pl-4 m-0">
                <li>The API must be reachable on a public HTTPS URL — Telegram requires it.</li>
                <li>Companies do not configure anything; this connection serves all of them.</li>
              </ul>
            </section>
          </aside>
        </div>
      )}
    </div>
  )
}
